import { Colorway } from '@uds/theme-utils';
import useGetSuggestions from 'domain/selectors/dst/useGetSuggestions';
import { lazy } from 'react';

import { getClassNames } from './DomainSearchSuggestions.stylesheet';
import DomainSearchSuggestion from './DomainSearchSuggestion';
import DomainSearchTypeLockup from './DomanSearchTypeLockup';
import Loader from './Loader';

const ExactSwitch = lazy(() => import('./ExactSwitch'));

export interface DomainSearchSuggestionsDesignProps {
  collapsePadding: {
    top: boolean;
    bottom: boolean;
  };
}
export interface DomainSearchSuggestionsTextProps {
  precursor: string;
  title: string;
  subtitle: string;
  domain: DomainSearchSuggestionsDomainProps;
  creativeSuggestions: DomainSearchSuggestionsCreativeProps;
  checkout: DomainSearchSuggestionsCheckoutProps;
  premium: DomainSearchSuggestionsPremiumProps;
  ad: DomainSearchSuggestionsAdProps;
}
export interface DomainSearchSuggestionsDomainProps {
  sale: {
    badge: string;
    savings: string;
  };
  price: string;
  recurringPrice: string;
  CTA: {
    label: string;
    analytics: string;
    glyph: string;
  };
}
export interface DomainSearchSuggestionsCreativeProps {
  enable: boolean;
  toggleOff: boolean;
  toggleDefault: boolean;
  label: string;
  analytics: string;
}
export interface DomainSearchSuggestionsCheckoutProps {
  CTA: {
    label: string;
    glyph: string;
    analytics: string;
  };
  glyph: {
    verificationState: string;
    unavailableState: string;
    addedState: string;
  };
  label: {
    addedState: string;
    unavailableState: string;
  };
}
export interface DomainSearchSuggestionsPremiumProps {
  enabled: boolean;
  badgeLabel: string;
  caveat: string;
}
export interface DomainSearchSuggestionsAdProps {
  enabled: boolean;
  badgeLabel: string;
}
export interface DomainSearchSuggestionsAnalyticsProps {
  type: string;
  view: string;
  productId: string;
}
export interface DomainSearchSuggestionsProps {
  colorway: Colorway;
  design: DomainSearchSuggestionsDesignProps;
  text: DomainSearchSuggestionsTextProps;
  visibilitySet: boolean;
  experiences: unknown;
  analytics: DomainSearchSuggestionsAnalyticsProps;
}

const DomainSearchSuggestions = (props: DomainSearchSuggestionsProps) => {
  const { design } = props;
  const classNames = getClassNames({ design });
  const { data: rawData, isLoading } = useGetSuggestions();
  const data = rawData?.body?.domains;
  const { enable: exactMatchActive } = props.text.creativeSuggestions;

  if (isLoading) {
    return <Loader />;
  }
  if (Array.isArray(data)) {
    return (
      <div className={classNames.root}>
        <div className={classNames.intro}>
          <div>
            <DomainSearchTypeLockup
              domains={data.length}
              eyebrow={props.text.precursor}
              title={props.text.title}
              subtitle={props.text.subtitle}
            />
          </div>
          {exactMatchActive && (
            <ExactSwitch
              analytics={props.text.creativeSuggestions.analytics}
              label={props.text.creativeSuggestions.label}
            />
          )}
        </div>
        <ul className={classNames.list}>
          {data.map((item, index) => {
            return <DomainSearchSuggestion key={item.domain_guid} item={item} index={index} {...props} />;
          })}
        </ul>
      </div>
    );
  }
  return null;
};

export default DomainSearchSuggestions;
